import { useEffect, useState } from 'react'
import { List, fromJS, Map } from 'immutable'
import _get from 'lodash/get'
import _isArray from 'lodash/isArray'
import {
  formatBillingPlans,
  BILLING_PLAN_GRID_LOCATION_CHECKOUT,
} from 'services/plans/utils'
import { useLazyQuery } from '@apollo/client'
import { GET_PLANS, GET_PLANS_BY_SKU } from 'services/plans/query'
import { get as getConfig } from 'config'
import {
  APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
  APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
} from 'services/apollo-graphql'

const config = getConfig()
const planGraphQLActive = !!_get(config, ['features', 'checkout', 'planGraphQL'])

/**
 * React hook for fetching plans for the plan grid (checkout, changePlan, gift)
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} option.authJwt - The user's auth jwt
 * @param {String} option.page - The location/page for the plan grid (checkout, etc)
 * @returns {List} - Formatted List of plans
 */
export function useGetPlanGridData (options) {
  const {
    userLanguage,
    authJwt,
    page = BILLING_PLAN_GRID_LOCATION_CHECKOUT,
  } = options
  const [getPlansData, { data: gqlPlanData, loading }] = useLazyQuery(GET_PLANS)
  const formattedPlans = fromJS(formatBillingPlans(gqlPlanData)) || List()
  const [planGridUserLanguage, setPlanGridUserLanguage] = useState(userLanguage)
  const [planGridAuthJwt, setPlanGridAuthJwt] = useState(authJwt)
  const userLanguageChanged = userLanguage !== planGridUserLanguage
  const authJwtChanged = authJwt !== planGridAuthJwt

  useEffect(() => {
    if (planGraphQLActive && !loading) {
      getPlansData({
        variables: {
          language: userLanguage,
          planGrid: true,
          page,
        },
        fetchPolicy: authJwtChanged
          ? APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [userLanguageChanged, authJwtChanged])

  // set the user language for this page to help handle loading plans
  // if the user changes their language while on this page
  useEffect(() => {
    setPlanGridUserLanguage(userLanguage)
  }, [userLanguage])

  // set the user's jwt for this page to help handle loading plans
  // if the user's jwt changes, then load plans again
  useEffect(() => {
    setPlanGridAuthJwt(authJwt)
  }, [authJwtChanged])

  return formattedPlans
}

/**
 * React hook for fetching plans by sku
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} option.authJwt - The user's auth jwt
 * @param {String|String[]} option.planSkus - A single sku (string) or an Array of sku strings
 * @returns {List} - Formatted List of plans
 */
export function useGetPlanDataBySku (options) {
  const { planSkus, userLanguage, authJwt } = options
  const [
    getPlansDataBySku,
    { data: planData, loading, error },
  ] = useLazyQuery(GET_PLANS_BY_SKU)
  const formattedPlans = fromJS(formatBillingPlans(planData)) || List()
  const [planGridUserLanguage, setPlanGridUserLanguage] = useState(userLanguage)
  const [planGridAuthJwt, setPlanGridAuthJwt] = useState(authJwt)
  const userLanguageChanged = userLanguage !== planGridUserLanguage
  const authJwtChanged = authJwt !== planGridAuthJwt
  const skus = _isArray(planSkus) ? planSkus : [planSkus]

  useEffect(() => {
    if (planGraphQLActive && planSkus) {
      getPlansDataBySku({
        variables: {
          language: userLanguage,
          skus,
        },
        fetchPolicy: authJwtChanged
          ? APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK
          : APOLLO_CLIENT_FETCH_POLICY_CACHE_FIRST,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [planSkus, userLanguageChanged, authJwtChanged])

  // set the user language for this page to help handle loading plans
  // if the user changes their language while on this page
  useEffect(() => {
    setPlanGridUserLanguage(userLanguage)
  }, [userLanguage])

  // set the user's jwt for this page to help handle loading plans
  // if the user's jwt changes, then load plans again
  useEffect(() => {
    setPlanGridAuthJwt(authJwt)
  }, [authJwtChanged])

  return Map({
    plans: formattedPlans,
    loading,
    error,
    complete: !loading && planData,
  })
}

/**
 * React hook for fetching plans for the plan grid (checkout, changePlan, gift)
 * @param {Object} options - The options
 * @param {String} options.userLanguage - The user's primary language
 * @param {String} option.authJwt - The user's auth jwt
 * @param {Map} option.selectedPlan - The user's selectedPlan
 * @param {String} option.userAccountCurrencyIso - The user's account currency iso
 * @param {Function} options.setPlansSelection - Set plan selection action
 */
export function useUpdateSelectedPlan (options) {
  const {
    selectedPlan = Map(),
    setPlansSelection,
    userLanguage,
    authJwt,
    userAccountCurrencyIso,
  } = options
  const [
    getPlansDataBySku,
    { data: planData },
  ] = useLazyQuery(GET_PLANS_BY_SKU)
  const [selectedPlanLanguage, setSelectedPlanLanguage] = useState(userLanguage)
  const selectedPlanSku = selectedPlan.get('sku')
  const userLanguageChanged = userLanguage !== selectedPlanLanguage
  const formattedPlans = fromJS(formatBillingPlans(planData)) || List()
  const incomingPlan = userLanguageChanged ? Map() : formattedPlans.find((p) => p.get('sku') === selectedPlanSku) || Map()

  useEffect(() => {
    if (planGraphQLActive && selectedPlanSku) {
      getPlansDataBySku({
        variables: {
          language: userLanguage,
          skus: selectedPlanSku,
        },
        fetchPolicy: APOLLO_CLIENT_FETCH_POLICY_CACHE_AND_NETWORK,
        context: {
          jwt: authJwt,
        },
      })
    }
  }, [userLanguageChanged, userAccountCurrencyIso])

  // set the user language for this the selected to help handle loading plans
  // if the user changes their language
  useEffect(() => {
    setSelectedPlanLanguage(userLanguage)
  }, [userLanguage])

  // set the user's selected plan in Redux if the language changed
  if (Map.isMap(incomingPlan) && incomingPlan.size && incomingPlan.get('billingSchedule') !== selectedPlan.get('billingSchedule')) {
    setPlansSelection(incomingPlan)
  }

  // set the user's selected plan if the currency changed
  if (Map.isMap(incomingPlan) && incomingPlan.size && incomingPlan.get('currencyIso') !== selectedPlan.get('currencyIso')) {
    setPlansSelection(incomingPlan)
  }
}
